/**
 * Widget Renderer - Renderiza widgets según su tipo
 *
 * Mapea el nombre del componente al componente React correspondiente.
 * Los títulos se construyen dinámicamente usando langStore.
 */

import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useRootStore } from '../../../store/contexts';
import { AvailableWidget } from '../../store/DashboardStore';
import { CountCard, GroupByChart, PieChart, SumCard, AvgCard, LineChart, WidgetWrapper } from '../base';
import { LangStore } from '../../../store/LangStore';
import { processWidgetDataWithLabels, DisplayIdMetadata } from '../../utils';
import { dayFirstLocale } from '../../../common/utils';

// Iconos para tipos de widgets
import { BarChartIcon, FunctionsIcon, PieChartIcon, ShowChartIcon, TrendingUpIcon } from "../../../icons";
export interface WidgetRendererProps {
  widget: AvailableWidget;
}

/**
 * Construye el título dinámico de un widget usando langStore.
 * Parsea el widgetKey para extraer entityName y fieldName.
 *
 * widgetKey format:
 * - count: "edition-count" → "Total Ediciones"
 * - groupBy: "edition-by-editionType" → "Ediciones por Tipo de Edición"
 */
function buildDynamicTitle(widget: AvailableWidget, langStore: LangStore): string {
  const { widgetKey, title: fallbackTitle } = widget;

  // Parsear widgetKey: "edition-count" o "edition-by-editionType"
  const countMatch = widgetKey.match(/^([a-z]+)-count$/i);
  const groupByMatch = widgetKey.match(/^([a-z]+)-by-(.+)$/i);

  if (countMatch) {
    // Widget de conteo: "Total {entityPlural}"
    const entityName = capitalize(countMatch[1]);
    const plural = getEntityPlural(langStore, entityName);
    return plural ? `Total ${plural}` : fallbackTitle;
  }

  if (groupByMatch) {
    // Widget de groupBy: "{entityPlural} por {fieldLabel}"
    const entityName = capitalize(groupByMatch[1]);
    const fieldPath = groupByMatch[2]; // puede ser "editionType" o "works-authors-author"

    const plural = getEntityPlural(langStore, entityName);
    if (!plural) return fallbackTitle;

    // Obtener el último campo del path (works-authors-author → author)
    const fieldParts = fieldPath.split('-');
    const lastField = fieldParts[fieldParts.length - 1];

    let fieldLabel: string;
    if (fieldParts.length > 1) {
      // Path anidado: usar targetEntityName del defaultConfig (ej: "AuthorComposer")
      const targetEntityName = widget.defaultConfig?.targetEntityName as string | undefined;
      if (targetEntityName) {
        fieldLabel = getEntitySingular(langStore, targetEntityName) || lastField;
      } else {
        fieldLabel = lastField;
      }
    } else {
      // Campo simple: buscar label del campo
      fieldLabel = langStore.getFieldLabel(entityName, lastField) || lastField;
    }

    return `${plural} por ${fieldLabel}`;
  }

  return fallbackTitle;
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getEntityPlural(langStore: LangStore, entityName: string): string | null {
  const pluralGetter = `${entityName}Plural`;
  const value = (langStore as any)[pluralGetter];
  return typeof value === 'string' ? value : null;
}

function getEntitySingular(langStore: LangStore, entityName: string): string | null {
  const singularGetter = `${entityName}Singular`;
  const value = (langStore as any)[singularGetter];
  return typeof value === 'string' ? value : null;
}

/**
 * Renderiza un widget según su tipo de componente
 */
export const WidgetRenderer: React.FC<WidgetRendererProps> = observer(
  ({ widget }) => {
    const { dashboardStore, langStore } = useRootStore();
    const loadingState = dashboardStore.getLoadingState(widget.widgetKey);
    const widgetData = dashboardStore.getWidgetData(widget.widgetKey);

    // Construir título dinámico usando langStore
    const widgetKeyForTitle = widget.widgetKey;
    const widgetTitle = widget.title;
    const language = langStore.language;
    const title = useMemo(
      () => buildDynamicTitle({ widgetKey: widgetKeyForTitle, title: widgetTitle } as AvailableWidget, langStore),
      // language included intentionally: recompute title when langStore.language mutates (mobx observable)
      // eslint-disable-next-line react-hooks/exhaustive-deps -- language is the reactivity trigger; langStore reference is stable
      [widgetKeyForTitle, widgetTitle, language, langStore]
    );

    // Cargar datos del widget al montar.
    // Pull-only: el server hace cache+invalidación por evento (ver
    // entityMutationsRedisBridge.ts); el cliente solo refetcha al
    // re-montar o al click del botón refresh — sin timer.
    const widgetKey = widget.widgetKey;
    useEffect(() => {
      // El container ya prefetchea todos los widgets en un batch; esto
      // solo cubre montadas sueltas y respeta la frescura local (no
      // repite el request al navegar ida y vuelta).
      dashboardStore.ensureWidgetData(widgetKey);
    }, [dashboardStore, widgetKey]);

    // Handler para refresh manual
    const handleRefresh = () => {
      dashboardStore.fetchWidgetData(widget.widgetKey);
    };

    // Renderizar según el tipo de componente
    switch (widget.component) {
      case 'CountCard':
        return renderCountCard(title, widgetData, loadingState, handleRefresh);

      case 'PieChart':
        return renderPieChart(title, widget, widgetData, loadingState, handleRefresh, langStore);

      case 'GroupByChart':
      case 'BarChart':
        return renderGroupByChart(
          title,
          widget,
          widgetData,
          loadingState,
          handleRefresh,
          langStore
        );

      case 'SumCard':
        return renderSumCard(title, widgetData, loadingState, handleRefresh);

      case 'AvgCard':
        return renderAvgCard(title, widgetData, loadingState, handleRefresh);

      case 'LineChart':
        return renderLineChart(title, widgetData, loadingState, handleRefresh, dayFirstLocale(language));

      default:
        return (
          <WidgetWrapper
            title={title}
            loading={loadingState.loading}
            computing={loadingState.computing}
            error={loadingState.error}
            onRefresh={handleRefresh}
          >
            <span>Componente no implementado: {widget.component}</span>
          </WidgetWrapper>
        );
    }
  }
);

/**
 * Renderiza un CountCard
 */
function renderCountCard(
  title: string,
  widgetData: any,
  loadingState: { loading: boolean; computing: boolean; error: string | null },
  onRefresh: () => void
) {
  const data = widgetData?.data as { count?: number; entityName?: string };
  const count = data?.count ?? 0;
  const entityName = data?.entityName;

  return (
    <CountCard
      title={title}
      count={count}
      entityName={entityName}
      loading={loadingState.loading}
      computing={loadingState.computing}
      error={loadingState.error}
      onRefresh={onRefresh}
      icon={<TrendingUpIcon color="primary" />}
    />
  );
}

/**
 * Renderiza un GroupByChart (barras horizontales)
 */
function renderGroupByChart(
  title: string,
  widget: AvailableWidget,
  widgetData: any,
  loadingState: { loading: boolean; computing: boolean; error: string | null },
  onRefresh: () => void,
  langStore: LangStore
) {
  const rawData = (widgetData?.data as Array<{
    groupKey: string | null;
    count: number;
    [key: string]: unknown;
  }>) || [];

  // Obtener metadata de displayId si existe
  const metadata = widgetData?.metadata as DisplayIdMetadata | undefined;

  // Procesar datos para agregar groupLabel
  const data = processDataWithLabels(rawData, metadata, langStore);

  return (
    <GroupByChart
      title={title}
      data={data}
      loading={loadingState.loading}
      computing={loadingState.computing}
      error={loadingState.error}
      onRefresh={onRefresh}
      icon={<BarChartIcon color="primary" />}
      maxItems={(widget.defaultConfig.limit as number) || 5}
    />
  );
}

/**
 * Procesa datos agregando labels según el tipo (workflow o entidad con displayId)
 */
function processDataWithLabels(
  data: Array<{ groupKey: string | null; count: number; [key: string]: unknown }>,
  metadata: DisplayIdMetadata | undefined,
  langStore: LangStore
): Array<{ groupKey: string; count: number; groupLabel: string; [key: string]: unknown }> {
  // Si es workflow, buscar labels en langStore
  if (metadata?.isWorkflow && metadata?.stateLabelsKey) {
    // stateLabelsKey es el nombre del getter en langStore, ej: "EditionDocumentationStateStateLabels"
    // Devuelve Record<string, string> como { docsGenerated: "Documentos Generados", ... }
    const stateLabels = (langStore as any)[metadata.stateLabelsKey] as Record<string, string> | undefined;
    return data.map((item) => {
      const stateId = item.groupKey || '';
      const label = stateLabels?.[stateId] || stateId;
      return {
        ...item,
        groupKey: stateId,
        groupLabel: label,
      };
    });
  }

  // Para entidades con displayId, usar la función de utils
  return processWidgetDataWithLabels(
    data.map(item => ({ ...item, groupKey: item.groupKey || '' })),
    metadata,
    langStore.language
  );
}

/**
 * Renderiza un PieChart (donut con total en centro)
 */
function renderPieChart(
  title: string,
  widget: AvailableWidget,
  widgetData: any,
  loadingState: { loading: boolean; computing: boolean; error: string | null },
  onRefresh: () => void,
  langStore: LangStore
) {
  const rawData = (widgetData?.data as Array<{
    groupKey: string | null;
    count: number;
    [key: string]: unknown;
  }>) || [];

  // Obtener metadata de displayId si existe
  const metadata = widgetData?.metadata as DisplayIdMetadata | undefined;

  // Procesar datos para agregar groupLabel (workflow o displayId)
  const data = processDataWithLabels(rawData, metadata, langStore);

  return (
    <PieChart
      title={title}
      data={data}
      loading={loadingState.loading}
      computing={loadingState.computing}
      error={loadingState.error}
      onRefresh={onRefresh}
      icon={<PieChartIcon color="primary" />}
      maxItems={(widget.defaultConfig.limit as number) || 10}
    />
  );
}

/**
 * Renderiza un SumCard
 */
function renderSumCard(
  title: string,
  widgetData: any,
  loadingState: { loading: boolean; computing: boolean; error: string | null },
  onRefresh: () => void
) {
  const data = widgetData?.data as { value?: number; field?: string };
  const value = data?.value ?? 0;
  const field = data?.field;

  return (
    <SumCard
      title={title}
      value={value}
      field={field}
      loading={loadingState.loading}
      computing={loadingState.computing}
      error={loadingState.error}
      onRefresh={onRefresh}
      icon={<FunctionsIcon color="primary" />}
    />
  );
}

/**
 * Renderiza un AvgCard
 */
function renderAvgCard(
  title: string,
  widgetData: any,
  loadingState: { loading: boolean; computing: boolean; error: string | null },
  onRefresh: () => void
) {
  const data = widgetData?.data as { value?: number; field?: string };
  const value = data?.value ?? 0;
  const field = data?.field;

  return (
    <AvgCard
      title={title}
      value={value}
      field={field}
      loading={loadingState.loading}
      computing={loadingState.computing}
      error={loadingState.error}
      onRefresh={onRefresh}
      icon={<FunctionsIcon color="info" />}
    />
  );
}

/**
 * Renderiza un LineChart para tendencias
 */
function renderLineChart(
  title: string,
  widgetData: any,
  loadingState: { loading: boolean; computing: boolean; error: string | null },
  onRefresh: () => void,
  locale: string
) {
  const data = (widgetData?.data as Array<{ date: string; count: number }>) || [];
  const metadata = widgetData?.metadata as { granularity?: 'day' | 'week' | 'month' };
  const granularity = metadata?.granularity || 'day';

  return (
    <LineChart
      title={title}
      data={data}
      loading={loadingState.loading}
      computing={loadingState.computing}
      error={loadingState.error}
      onRefresh={onRefresh}
      icon={<ShowChartIcon color="primary" />}
      granularity={granularity}
      locale={locale}
    />
  );
}

export default WidgetRenderer;
